import React, { useCallback, useEffect, useState, useContext } from 'react';
import {
  Alert, Button, Descriptions, Input, Popconfirm, Segmented, Select, Spin, Table, Tag, Typography, Upload, message,
} from 'antd';
import { InboxOutlined, CloudUploadOutlined, DatabaseOutlined, IdcardOutlined } from '@ant-design/icons';
import api from '../api';
import { UserContext, hasGrant } from '../App';

const { Dragger } = Upload;
const { Title, Text, Paragraph } = Typography;

const asPct    = (v) => v != null ? `${Number(v).toFixed(2)}%` : '—';

// CNE started scoring externally in September 2026, so nothing before it can
// have a QA export. Filtered here rather than on /api/admin/cne-month-keys,
// which the handle-time upload shares and does need the earlier months.
const FIRST_MONTH = '2026-09';

function ChangeCell({ value }) {
  if (value == null) return <Text type="secondary">—</Text>;
  const color = value < 0 ? '#ff4d4f' : value > 0 ? '#52c41a' : undefined;
  return <span style={{ color }}>{value > 0 ? '+' : ''}{Number(value).toFixed(2)}%</span>;
}

export default function CNEMonthlyQAStatsUploadPage() {
  const currentUser = useContext(UserContext);
  // Role, or an explicit grant for this page. The grant is additive, so it
  // opens the page for someone outside the CNE team and never closes it.
  const isAdmin     = currentUser?.Role === 'admin' || currentUser?.Role === 'qa_admin'
                   || (currentUser?.JobID === 6 && currentUser?.TeamID === 3)
                   || hasGrant(currentUser, 'cne-qa-upload');

  const [view, setView] = useState('upload');

  // Same month list the handle-time upload uses, so both exports are filed
  // against the same set of months.
  const [monthKeys,        setMonthKeys]        = useState([]);
  const [monthKeysLoading, setMonthKeysLoading] = useState(true);
  const [selectedMonth,    setSelectedMonth]    = useState(null);

  const [file,        setFile]        = useState(null);
  const [preview,     setPreview]     = useState(null);
  const [previewing,  setPreviewing]  = useState(false);
  const [assignments, setAssignments] = useState({});
  const [saving,      setSaving]      = useState(false);
  const [result,      setResult]      = useState(null);

  const [uploaded,        setUploaded]        = useState([]);
  const [uploadedLoading, setUploadedLoading] = useState(false);
  const [uploadedMonth,   setUploadedMonth]   = useState(null);

  const [teamUsers,      setTeamUsers]      = useState([]);
  const [unmapped,       setUnmapped]       = useState([]);
  const [mappingLoading, setMappingLoading] = useState(false);
  const [awsIdDraft,     setAwsIdDraft]     = useState({});
  const [savingIds,      setSavingIds]      = useState(new Set());

  useEffect(() => {
    api.get('/api/admin/cne-month-keys')
      .then((r) => setMonthKeys((r.data || []).filter((k) => k >= FIRST_MONTH)))
      .catch(() => {})
      .finally(() => setMonthKeysLoading(false));
  }, []);

  useEffect(() => {
    if (view !== 'uploaded' || !uploadedMonth) { setUploaded([]); return; }
    setUploadedLoading(true);
    api.get('/api/admin/cne-qa/uploaded', { params: { month_key: uploadedMonth } })
      .then((r) => setUploaded(r.data))
      .catch(() => setUploaded([]))
      .finally(() => setUploadedLoading(false));
  }, [view, uploadedMonth]);

  // The same QAUsers.AWSID the handle-time upload maintains -- one field, two
  // places to reach it, so an unmapped agent can be fixed without leaving the
  // upload that surfaced them.
  const loadMapping = useCallback(() => {
    if (view !== 'aws-ids') return;
    setMappingLoading(true);
    Promise.all([
      api.get('/api/admin/cne-team-users'),
      api.get('/api/admin/cne-qa/unmapped').catch(() => ({ data: [] })),
    ])
      .then(([usersRes, unmappedRes]) => {
        setTeamUsers(usersRes.data);
        setUnmapped(unmappedRes.data || []);
        const draft = {};
        (usersRes.data || []).forEach((u) => { draft[u.ID] = u.AWSID || ''; });
        setAwsIdDraft(draft);
      })
      .catch(() => message.error('Failed to load team users.'))
      .finally(() => setMappingLoading(false));
  }, [view]);

  useEffect(() => { loadMapping(); }, [loadMapping]);

  const saveAwsId = async (userId) => {
    setSavingIds((prev) => new Set([...prev, userId]));
    try {
      await api.put('/api/admin/cne-aws-id', { user_id: userId, aws_id: awsIdDraft[userId] || null });
      message.success('AWS ID updated.');
      loadMapping();
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to update AWS ID.');
    } finally {
      setSavingIds((prev) => { const s = new Set(prev); s.delete(userId); return s; });
    }
  };

  const doPreview = async (f) => {
    setPreviewing(true);
    setPreview(null);
    setResult(null);
    const form = new FormData();
    form.append('file', f);
    try {
      const res = await api.post('/api/admin/cne-qa/preview', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(res.data);
      const seed = {};
      (res.data.agents || []).forEach((a) => { seed[a.AWSID] = a.SuggestedCampaign; });
      setAssignments(seed);
    } catch (err) {
      message.error(err.response?.data?.error || 'Could not read that file.');
      setFile(null);
    } finally {
      setPreviewing(false);
    }
  };

  const doCommit = async () => {
    if (!selectedMonth) { message.warning('Select a month first.'); return; }
    setSaving(true);
    const form = new FormData();
    form.append('file', file);
    form.append('month_key', selectedMonth);
    form.append('assignments', JSON.stringify(assignments));
    try {
      const res = await api.post('/api/admin/cne-qa/commit', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(res.data);
      message.success(`Saved ${res.data.inserted} rows for ${selectedMonth}.`);
      setPreview(null);
      setFile(null);
    } catch (err) {
      message.error(err.response?.data?.error || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const campaignOptions = (preview?.campaigns || []).map((c) => ({ label: c.Name, value: c.Name }));
  const changedCount = (preview?.agents || [])
    .filter((a) => assignments[a.AWSID] && assignments[a.AWSID] !== a.SuggestedCampaign).length;

  const previewCols = [
    { title: 'Agent', key: 'agent', width: 230,
      render: (_, a) => (
        <span>
          {a.DisplayName
            ? <><strong>{a.DisplayName}</strong>{a.IsActive === false && <Tag color="default" style={{ marginLeft: 6 }}>Inactive</Tag>}
                <br /><Text type="secondary" style={{ fontSize: 11 }}>{a.AWSID}</Text></>
            : <Text type="warning">{a.AWSID} <Tag color="orange">No match</Tag></Text>}
        </span>
      ),
    },
    { title: 'Campaign', key: 'campaign', width: 200,
      render: (_, a) => (
        <div>
          <Select
            size="small"
            style={{ width: 170 }}
            options={campaignOptions}
            value={assignments[a.AWSID]}
            onChange={(v) => setAssignments((prev) => ({ ...prev, [a.AWSID]: v }))}
          />
          {assignments[a.AWSID] !== a.SuggestedCampaign && (
            <div style={{ fontSize: 11, color: '#fa8c16' }}>changed from {a.SuggestedCampaign}</div>
          )}
        </div>
      ),
    },
    { title: 'Evaluation Form', dataIndex: 'EvaluationForm', key: 'EvaluationForm', width: 190, ellipsis: true,
      // A blank form is the month's rollup for that agent, and the only row the
      // monthly stats refresh reads. Named rows are that form's slice of the
      // same month, stored for reference.
      render: (v) => v || <Tag color="blue">Month rollup</Tag> },
    { title: 'Avg Score', dataIndex: 'AvgEvalScore', key: 'AvgEvalScore', width: 105, align: 'right', render: asPct },
    { title: 'Evals', dataIndex: 'EvalsPerformed', key: 'EvalsPerformed', width: 75, align: 'right' },
    { title: 'Change', dataIndex: 'PctChange', key: 'PctChange', width: 95, align: 'right',
      render: (v) => <ChangeCell value={v} /> },
    { title: 'Prior Avg', dataIndex: 'PriorAvgEvalScore', key: 'PriorAvgEvalScore', width: 105, align: 'right', render: asPct },
    { title: 'Auto Fails', dataIndex: 'AutoFailsPct', key: 'AutoFailsPct', width: 105, align: 'right',
      render: (v) => v == null ? <Text type="secondary">—</Text>
        : v > 0 ? <Tag color="red">{Number(v).toFixed(2)}%</Tag> : '0.00%' },
  ];

  const uploadedCols = [
    { title: 'Agent', key: 'agent', width: 230,
      render: (_, r) => (
        <span>
          {r.DisplayName
            ? <><strong>{r.DisplayName}</strong><br /><Text type="secondary" style={{ fontSize: 11 }}>{r.AWSID}</Text></>
            : <Text type="warning">{r.AWSID} <Tag color="orange">No match</Tag></Text>}
        </span>
      ),
    },
    { title: 'Campaign', dataIndex: 'CampaignName', key: 'CampaignName', width: 150,
      sorter: (a, b) => (a.CampaignName || '').localeCompare(b.CampaignName || '') },
    { title: 'Evaluation Form', dataIndex: 'EvaluationForm', key: 'EvaluationForm', width: 190, ellipsis: true,
      render: (v) => v || <Tag color="blue">Month rollup</Tag> },
    { title: 'Avg Score', dataIndex: 'AvgEvalScore', key: 'AvgEvalScore', width: 105, align: 'right',
      sorter: (a, b) => (a.AvgEvalScore ?? -1) - (b.AvgEvalScore ?? -1), render: asPct },
    { title: 'Evals', dataIndex: 'EvalsPerformed', key: 'EvalsPerformed', width: 75, align: 'right',
      sorter: (a, b) => (a.EvalsPerformed ?? 0) - (b.EvalsPerformed ?? 0) },
    { title: 'Change', dataIndex: 'PctChange', key: 'PctChange', width: 95, align: 'right',
      sorter: (a, b) => (a.PctChange ?? 0) - (b.PctChange ?? 0),
      render: (v) => <ChangeCell value={v} /> },
    { title: 'Prior Avg', dataIndex: 'PriorAvgEvalScore', key: 'PriorAvgEvalScore', width: 105, align: 'right', render: asPct },
    { title: 'Auto Fails', dataIndex: 'AutoFailsPct', key: 'AutoFailsPct', width: 105, align: 'right',
      render: (v) => v == null ? <Text type="secondary">—</Text>
        : v > 0 ? <Tag color="red">{Number(v).toFixed(2)}%</Tag> : '0.00%' },
    { title: 'Uploaded', dataIndex: 'UploadedAt', key: 'UploadedAt', width: 160,
      render: (v) => v ? v.replace('T', ' ').substring(0, 16) : '—' },
  ];

  const userCols = [
    { title: 'Name', key: 'name', width: 240,
      render: (_, r) => (
        <span>
          {r.DisplayName}
          {!r.IsActive && <Tag color="default" style={{ marginLeft: 8 }}>Inactive</Tag>}
        </span>
      ),
    },
    { title: 'AWS Agent ID', key: 'awsid',
      render: (_, r) => (
        <Input
          size="small"
          value={awsIdDraft[r.ID] ?? ''}
          placeholder="e.g. TSmith@0054N000005Dbc3@00D410000006xvV"
          style={{ fontFamily: 'monospace', fontSize: 12 }}
          onChange={(e) => setAwsIdDraft((prev) => ({ ...prev, [r.ID]: e.target.value }))}
          onPressEnter={() => saveAwsId(r.ID)}
          allowClear
        />
      ),
    },
    { title: '', key: 'save', width: 80,
      render: (_, r) => {
        const changed = (awsIdDraft[r.ID] ?? '') !== (r.AWSID || '');
        return (
          <Button
            size="small"
            type={changed ? 'primary' : 'default'}
            loading={savingIds.has(r.ID)}
            disabled={!changed}
            onClick={() => saveAwsId(r.ID)}
          >
            Save
          </Button>
        );
      },
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>CNE Monthly QA Stats</Title>
        <Segmented
          value={view}
          onChange={setView}
          options={[
            { label: 'Upload',                 value: 'upload',   icon: <CloudUploadOutlined /> },
            { label: 'View Uploaded',          value: 'uploaded', icon: <DatabaseOutlined /> },
            { label: 'Update AWS Agent IDs',   value: 'aws-ids',  icon: <IdcardOutlined /> },
          ]}
        />
      </div>

      {view === 'upload' && (
        <>
          <Paragraph type="secondary" style={{ marginBottom: 20 }}>
            Upload the CNE agent evaluation metrics export (tab-separated). The first column must be <strong>Agent</strong>.
            The export carries no campaign, so one is suggested per agent from their job and can be changed before saving —
            the same as the daily upload. Agents are matched on the <strong>AWS ID</strong> field of their user record; the
            export appends their name to that id and only the id is stored, so a later name change never breaks the link.
          </Paragraph>

          {!isAdmin && (
            <Alert type="warning" message="Admin access required to upload data." style={{ marginBottom: 16 }} />
          )}

          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 20, flexWrap: 'wrap' }}>
            <Text strong>Month:</Text>
            {monthKeysLoading
              ? <Spin size="small" />
              : (
                <Select
                  style={{ minWidth: 160 }}
                  placeholder="Select month"
                  value={selectedMonth}
                  onChange={setSelectedMonth}
                  options={monthKeys.map((k) => ({ label: k, value: k }))}
                  showSearch
                />
              )
            }
          </div>

          {isAdmin && !preview && (
            <Dragger
              name="file"
              accept=".txt,.tsv,.csv"
              beforeUpload={(f) => { setFile(f); doPreview(f); return false; }}
              onRemove={() => { setFile(null); setPreview(null); }}
              fileList={file ? [file] : []}
              disabled={previewing}
              style={{ marginBottom: 16 }}
            >
              <p className="ant-upload-drag-icon"><InboxOutlined /></p>
              <p className="ant-upload-text">Click or drag a tab-delimited .txt file here</p>
              <p className="ant-upload-hint">
                First column must be &quot;Agent&quot;. Nothing is saved until you confirm the campaigns on the next step.
              </p>
            </Dragger>
          )}

          {previewing && <div style={{ padding: 24, textAlign: 'center' }}><Spin /></div>}

          {preview && (
            <>
              <Alert
                type={preview.unmatched?.length ? 'warning' : 'info'}
                style={{ marginBottom: 16 }}
                message={preview.unmatched?.length
                  ? `${preview.unmatched.length} agent(s) have no matching AWS ID in QAUsers`
                  : 'Ready to save'}
                description={
                  <>
                    <Descriptions size="small" column={2} style={{ marginTop: 8 }}>
                      <Descriptions.Item label="Agents">{preview.row_count}</Descriptions.Item>
                      <Descriptions.Item label="Month">{selectedMonth || <Text type="danger">not selected</Text>}</Descriptions.Item>
                    </Descriptions>
                    {preview.unmatched?.length > 0 && (
                      <>
                        <p style={{ margin: '8px 0 4px' }}>
                          These save anyway. Nothing is lost while they wait, but their score will not reach
                          Agent Monthly Stats until the id is mapped &mdash; set it on the{' '}
                          <Button type="link" size="small" style={{ padding: 0 }} onClick={() => setView('aws-ids')}>
                            Update AWS Agent IDs
                          </Button>{' '}
                          tab, and every month that id has ever appeared in links on the next refresh.
                        </p>
                        <div style={{ maxHeight: 140, overflowY: 'auto', fontFamily: 'monospace', fontSize: 12, background: 'rgba(0,0,0,0.04)', padding: '8px 12px', borderRadius: 4 }}>
                          {preview.unmatched.map((id) => <div key={id}>{id}</div>)}
                        </div>
                      </>
                    )}
                  </>
                }
              />

              <Title level={5} style={{ marginBottom: 8 }}>
                Confirm campaigns
                {changedCount > 0 && (
                  <Text type="secondary" style={{ fontWeight: 400, marginLeft: 8 }}>
                    {changedCount} changed from the suggestion
                  </Text>
                )}
              </Title>
              <Table
                rowKey={(a) => `${a.AWSID}||${a.EvaluationForm ?? ''}`}
                dataSource={preview.agents}
                columns={previewCols}
                size="small"
                pagination={false}
                scroll={{ x: 'max-content' }}
                bordered
                style={{ marginBottom: 16 }}
              />

              <div style={{ display: 'flex', gap: 8 }}>
                <Popconfirm
                  title={`Save ${preview.row_count} rows for ${selectedMonth || '—'}?`}
                  description="Everything already stored for this month is replaced."
                  okText="Save"
                  disabled={!selectedMonth}
                  onConfirm={doCommit}
                >
                  <Button type="primary" loading={saving} disabled={!selectedMonth}>
                    Save {preview.row_count} Rows
                  </Button>
                </Popconfirm>
                <Button onClick={() => { setPreview(null); setFile(null); }} disabled={saving}>
                  Cancel
                </Button>
              </div>
            </>
          )}

          {result && (
            <Alert
              type="success"
              style={{ marginTop: 20 }}
              message={`Upload complete for ${selectedMonth}`}
              description={
                <Descriptions size="small" column={3} style={{ marginTop: 8 }}>
                  <Descriptions.Item label="Rows saved">{result.inserted}</Descriptions.Item>
                  <Descriptions.Item label="Matched to user">{result.matched}</Descriptions.Item>
                  <Descriptions.Item label="Unmatched">
                    {result.unmatched > 0
                      ? <Tag color="orange">{result.unmatched} — set their AWS ID to link</Tag>
                      : <Tag color="green">0</Tag>}
                  </Descriptions.Item>
                </Descriptions>
              }
            />
          )}
        </>
      )}

      {/* --- AWS ID MAPPING --------------------------------------------- */}
      {view === 'aws-ids' && (
        <>
          <Paragraph type="secondary" style={{ marginBottom: 16 }}>
            Set the <strong>AWS Agent ID</strong> for each CNE team member. This is the same field the
            CNE Stats upload uses, so setting it here fixes both. Nothing needs re-uploading afterwards:
            stored rows are matched on the id at read time, so filling one in links every month it has
            ever appeared in.
          </Paragraph>

          {unmapped.length > 0 && (
            <Alert
              type="warning"
              style={{ marginBottom: 16 }}
              message={`${unmapped.length} AWS ID(s) in stored QA rows belong to nobody yet`}
              description={
                <Table
                  size="small"
                  rowKey="AWSID"
                  dataSource={unmapped}
                  pagination={false}
                  style={{ marginTop: 8 }}
                  columns={[
                    { title: 'AWS Agent ID', dataIndex: 'AWSID', key: 'AWSID',
                      render: (v) => <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</span> },
                    { title: 'Months', key: 'months', width: 180,
                      render: (_, r) => r.FirstMonth === r.LastMonth ? r.FirstMonth : `${r.FirstMonth} to ${r.LastMonth}` },
                    { title: 'Rows', dataIndex: 'Rows', key: 'Rows', width: 80, align: 'right' },
                  ]}
                />
              }
            />
          )}

          {mappingLoading
            ? <Spin />
            : (
              <Table
                rowKey="ID"
                dataSource={teamUsers}
                columns={userCols}
                size="small"
                pagination={false}
                bordered
                rowClassName={(r) => !r.IsActive ? 'ant-table-row-muted' : ''}
              />
            )
          }
        </>
      )}

      {view === 'uploaded' && (
        <>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
            <Text strong>Month:</Text>
            <Select
              style={{ minWidth: 160 }}
              placeholder="Select month"
              value={uploadedMonth}
              onChange={setUploadedMonth}
              options={monthKeys.map((k) => ({ label: k, value: k }))}
              showSearch
            />
            {uploaded.length > 0 && <Text type="secondary">{uploaded.length} agents</Text>}
          </div>

          {uploadedLoading
            ? <Spin />
            : !uploadedMonth
              ? <Text type="secondary">Pick a month to see what is stored.</Text>
              : uploaded.length === 0
                ? <Text type="secondary">No data uploaded for this month yet.</Text>
                : (
                  <Table
                    rowKey="ID"
                    dataSource={uploaded}
                    columns={uploadedCols}
                    size="small"
                    pagination={false}
                    scroll={{ x: 'max-content' }}
                    bordered
                  />
                )
          }
        </>
      )}
    </>
  );
}
